import { inject, Injectable } from "@angular/core";
import { DOCUMENT, Location } from "@angular/common";
import {
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalInterceptorAuthRequest,
    MsalInterceptorConfiguration,
    MsalService,
    ProtectedResourceScopes
} from "@azure/msal-angular";
import { AccountInfo, AuthenticationResult, InteractionStatus, InteractionType } from "@azure/msal-browser";
import { Chat } from "@hashbrownai/core";
import { catchError, EMPTY, filter, firstValueFrom, Observable, of, switchMap, take } from "rxjs";

@Injectable()
export class MsalHashbrownMiddleware {
    private static readonly DEFAULT_CORRELATION_ID = "";

    private authService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private _document = inject(DOCUMENT);
    private location = inject(Location);
    private msalInterceptorConfig = inject<MsalInterceptorConfiguration>(MSAL_INTERCEPTOR_CONFIG);

    async addBearerToken(url: string, req: RequestInit): Promise<RequestInit> {
        this.authService.getLogger().verbose("MSAL Hashbrown Middleware activated", MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);
        const scopes = this.getScopesForEndpoint(url, "POST");

        if (!scopes || scopes.length === 0) {
            this.authService
                .getLogger()
                .verbose("Hashbrown Middleware - no scopes for endpoint", MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);
            return Promise.resolve(req);
        }

        let account: AccountInfo | undefined;
        if (this.authService.instance.getActiveAccount()) {
            this.authService
                .getLogger()
                .verbose("Hashbrown Middleware - active account selected", MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);
            account = this.authService.instance.getActiveAccount() ?? undefined;
        } else {
            this.authService
                .getLogger()
                .verbose(
                    "Hashbrown Middleware - no active account, fallback to first account",
                    MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
                );
            account = this.authService.instance.getAllAccounts()[0];
        }

        const authRequest = { ...this.msalInterceptorConfig.authRequest, account };

        this.authService
            .getLogger()
            .info(`Hashbrown Middleware - ${scopes.length} scopes found for endpoint`, MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);
        this.authService
            .getLogger()
            .infoPii(`Hashbrown Middleware - [${scopes}] scopes found for ${url}`, MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);

        return await firstValueFrom(
            this.acquireToken(authRequest, scopes, account).pipe(
                switchMap((result: AuthenticationResult) => {
                    this.authService
                        .getLogger()
                        .verbose("Hashbrown Middleware - setting authorization headers", MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);
                    req.headers = { ...req.headers, ["Authorization"]: `Bearer ${result.accessToken}` };

                    return of(req);
                })
            )
        );
    }

    private getScopesForEndpoint(endpoint: string, httpMethod: string): Array<string> | null {
        this.authService
            .getLogger()
            .verbose("Hashbrown Middleware: getting scopes for endpoint", MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID);

        const normalizedEndpoint = this.location.normalize(endpoint);

        const protectedResourcesArray = Array.from(this.msalInterceptorConfig.protectedResourceMap.keys());

        const matchingProtectedResources = this.matchResourcesToEndpoint(protectedResourcesArray, normalizedEndpoint);

        if (matchingProtectedResources.length > 0) {
            return this.matchScopesToEndpoint(this.msalInterceptorConfig.protectedResourceMap, matchingProtectedResources, httpMethod);
        }

        return null;
    }

    private acquireToken(
        authRequest: MsalInterceptorAuthRequest,
        scopes: string[],
        account: AccountInfo | undefined
    ): Observable<AuthenticationResult> {
        return this.authService.acquireTokenSilent({ ...authRequest, scopes, account }).pipe(
            catchError(() => {
                this.authService
                    .getLogger()
                    .error(
                        "Hashbrown Middleware - acquireTokenSilent rejected with error. Invoking interaction to resolve.",
                        authRequest.correlationId ?? MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
                    );
                return this.msalBroadcastService.inProgress$.pipe(
                    take(1),
                    switchMap((status: InteractionStatus) => {
                        if (status === InteractionStatus.None) {
                            return this.acquireTokenInteractively(authRequest, scopes);
                        }

                        return this.msalBroadcastService.inProgress$.pipe(
                            filter((status: InteractionStatus) => status === InteractionStatus.None),
                            take(1),
                            switchMap(() => this.acquireToken(authRequest, scopes, account))
                        );
                    })
                );
            }),
            switchMap((result: AuthenticationResult) => {
                if (!result.accessToken) {
                    this.authService
                        .getLogger()
                        .error(
                            "Hashbrown Middleware - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.",
                            authRequest.correlationId ?? MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
                        );
                    return this.msalBroadcastService.inProgress$.pipe(
                        filter((status: InteractionStatus) => status === InteractionStatus.None),
                        take(1),
                        switchMap(() => this.acquireTokenInteractively(authRequest, scopes))
                    );
                }
                return of(result);
            })
        );
    }

    private acquireTokenInteractively(authRequest: MsalInterceptorAuthRequest, scopes: string[]): Observable<AuthenticationResult> {
        if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
            this.authService
                .getLogger()
                .verbose(
                    "Hashbrown Middleware - error acquiring token silently, acquiring by popup",
                    authRequest.correlationId ?? MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
                );
            return this.authService.acquireTokenPopup({ ...authRequest, scopes });
        }
        this.authService
            .getLogger()
            .verbose(
                "Hashbrown Middleware - error acquiring token silently, acquiring by redirect",
                authRequest.correlationId ?? MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
            );
        const redirectStartPage = window.location.href;
        this.authService.acquireTokenRedirect({
            ...authRequest,
            scopes,
            redirectStartPage
        });
        return EMPTY;
    }

    private matchResourcesToEndpoint(protectedResourcesEndpoints: string[], endpoint: string): Array<string> {
        const matchingResources: Array<string> = [];

        protectedResourcesEndpoints.forEach((key) => {
            const normalizedKey = this.location.normalize(key);

            const absoluteKey = this.getAbsoluteUrl(normalizedKey);
            const keyComponents = new URL(absoluteKey);
            const absoluteEndpoint = this.getAbsoluteUrl(endpoint);
            const endpointComponents = new URL(absoluteEndpoint);

            if (this.checkUrlComponents(keyComponents, endpointComponents)) {
                matchingResources.push(key);
            }
        });

        return matchingResources;
    }

    private checkUrlComponents(keyComponents: URL, endpointComponents: URL): boolean {
        // URL properties from https://developer.mozilla.org/en-US/docs/Web/API/URL
        const urlProperties = ["protocol", "host", "pathname", "search", "hash"];
        const componentMap = {
            protocol: "protocol",
            host: "host",
            pathname: "path",
            search: "search",
            hash: "hash"
        } as const;
        const useStrictMatching = this.msalInterceptorConfig.strictMatching !== false;

        for (const property of urlProperties) {
            const _property = property as keyof URL;
            if (keyComponents[_property]) {
                const decodedInput = decodeURIComponent(keyComponents[_property] as string);
                const endpointComponent = endpointComponents[_property] as string;

                if (useStrictMatching) {
                    const component = componentMap[_property as keyof typeof componentMap];

                    if (!this.matchPatternStrict(decodedInput, endpointComponent, component)) {
                        return false;
                    }
                } else if (!this.matchPattern(decodedInput, endpointComponent)) {
                    return false;
                }
            }
        }

        return true;
    }

    private getAbsoluteUrl(url: string): string {
        const link = this._document.createElement("a");
        link.href = url;
        return link.href;
    }

    private matchScopesToEndpoint(
        protectedResourceMap: Map<string, Array<string | ProtectedResourceScopes> | null>,
        endpointArray: string[],
        httpMethod: string
    ): Array<string> | null {
        const allMatchedScopes: Array<string[] | null> = [];

        // Check each matched endpoint for matching HttpMethod and scopes
        endpointArray.forEach((matchedEndpoint) => {
            const scopesForEndpoint: string[] = [];
            const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);

            // Return if resource is unprotected
            if (methodAndScopesArray === null || methodAndScopesArray === undefined) {
                allMatchedScopes.push(null);
                return;
            }

            methodAndScopesArray.forEach((entry) => {
                // Entry is either array of scopes or ProtectedResourceScopes object
                if (typeof entry === "string") {
                    scopesForEndpoint.push(entry);
                } else {
                    // Ensure methods being compared are normalized
                    const normalizedRequestMethod = httpMethod.toLowerCase();
                    const normalizedResourceMethod = entry.httpMethod.toLowerCase();
                    // Method in protectedResourceMap matches request http method
                    if (normalizedResourceMethod === normalizedRequestMethod) {
                        // Validate if scopes comes null to unprotect the resource in a certain http method
                        if (entry.scopes === null) {
                            allMatchedScopes.push(null);
                        } else {
                            entry.scopes.forEach((scope) => {
                                scopesForEndpoint.push(scope);
                            });
                        }
                    }
                }
            });

            // Only add to all scopes if scopes for endpoint and method is found
            if (scopesForEndpoint.length > 0) {
                allMatchedScopes.push(scopesForEndpoint);
            }
        });

        if (allMatchedScopes.length > 0) {
            if (allMatchedScopes.length > 1) {
                this.authService
                    .getLogger()
                    .warning(
                        "Hashbrown Middleware - More than 1 matching scopes for endpoint found.",
                        MsalHashbrownMiddleware.DEFAULT_CORRELATION_ID
                    );
            }
            // Returns scopes for first matching endpoint
            return allMatchedScopes[0];
        }

        return null;
    }

    private matchPattern(pattern: string, input: string): boolean {
        const regex = new RegExp(pattern.replace(/\\/g, "\\\\").replace(/\*/g, "[^ ]*").replace(/\?/g, "\\?"));

        return regex.test(input);
    }

    private matchPatternStrict(pattern: string, input: string, component: "protocol" | "host" | "path" | "search" | "hash"): boolean {
        let regexBody = pattern.replace(/[.+^${}()|[\]\\*?]/g, "\\$&");

        regexBody = component === "host" ? regexBody.replace(/\\\*/g, "[^.]*") : regexBody.replace(/\\\*/g, ".*");

        const regex = new RegExp(`^${regexBody}$`);

        return regex.test(input);
    }
}

export function msalHashbrownMiddleware(url: string): Chat.Middleware {
    return async (request: RequestInit): Promise<RequestInit> => {
        const handler = inject(MsalHashbrownMiddleware);
        return await handler.addBearerToken(url, request);
    };
}
